import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaFacebook,
  FaWhatsapp,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import "./CatalogueProductsDetails-cat.css";
import { API_BASE_URL, CART_API_URL } from "../config/api";
import Seo from "../Components/Seo";
import { authedFetch } from "../services/apiClient";

export default function CatalogueProductsDetailsCat() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [adminNumbers, setAdminNumbers] = useState([]);
  const [inCart, setInCart] = useState(false);
  const [cartUpdating, setCartUpdating] = useState(false);

  const shareRef = useRef(null);
  const mobileNo = localStorage.getItem("userMobileNumber") || "";

  const isAdmin = adminNumbers.includes(mobileNo);

  /* ---------------- FETCH PRODUCT ---------------- */
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/Product/GetProductById?id=${productId}`
        );
        const data = await res.json();
        setProduct(data?.[0] || null);
      } catch {
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/Admin/GetAllAdmins`
        );
        if (!res.ok) throw new Error("Admin fetch failed");
        const data = await res.json();
        if (Array.isArray(data)) {
          const numbers = data
            .map((a) => a?.MobileNumber)
            .filter(Boolean)
            .map(String);
          setAdminNumbers(numbers);
        }
      } catch (err) {
        console.error("Failed to load admins", err);
        setAdminNumbers([]);
      }
    };
    fetchAdmins();
  }, []);

  /* ---------------- CART ---------------- */
  useEffect(() => {
    const checkCart = async () => {
      if (!mobileNo) return;
      try {
        const res = await authedFetch(
          `${CART_API_URL}/Cart/GetCartProducts?mobileNo=${mobileNo}`
        );
        if (!res.ok) throw new Error("Cart fetch failed");
        const data = await res.json();
        const ids =
          data?.products?.map((p) => String(p?.id ?? p?.Id)).filter(Boolean) ||
          [];
        setInCart(ids.includes(String(productId)));
      } catch (err) {
        console.log("Error loading cart", err);
      }
    };
    if (product) checkCart();
  }, [product, mobileNo, productId]);

  const handleCartToggle = async () => {
    if (!mobileNo) {
      navigate("/login");
      return;
    }
    try {
      setCartUpdating(true);
      const res = await authedFetch(`${CART_API_URL}/Cart/UpdateCart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo,
          productId,
          action: inCart ? "removeProduct" : "addProduct",
        }),
      });
      if (!res.ok) throw new Error("Cart update failed");
      setInCart((v) => !v);
    } catch (err) {
      console.log("Error updating cart", err);
      alert("Unable to update cart");
    } finally {
      setCartUpdating(false);
    }
  };

  const handleBuyNow = () => {
    if (!mobileNo) {
      navigate("/login");
      return;
    }
    navigate("/cart");
  };

  /* ---------------- CLOSE SHARE POPUP ---------------- */
  useEffect(() => {
    const handler = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShare(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ---------------- SHARE HANDLERS ---------------- */
  const productUrl = `${window.location.origin}/product-details/${productId}`;

  const shareFacebook = () =>
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        productUrl
      )}`,
      "_blank"
    );

  const shareWhatsapp = () =>
    window.open(
      `https://wa.me/?text=${encodeURIComponent(
        `${product.ProductName}\n₹${product.Price}\n${product.Description}\n${productUrl}`
      )}`,
      "_blank"
    );

  /* ---------------- DELETE ---------------- */
  const deleteProduct = async () => {
    if (!isAdmin) {
      alert("You are not authorized to delete this product.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this product?")) return;

    const res = await authedFetch(
      `${API_BASE_URL}/Product/MoveToBin?id=${productId}`,
      { method: "PUT" }
    );
    if (!res.ok) {
      alert("Delete failed — server ne reject kiya");
      return;
    }
    alert("Product moved to Recycle Bin");
    navigate("/catalogue");
  };

  // ---------------- IMAGE CAROUSEL ----------------
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = useMemo(() => {
    if (!product) return [];
    const list = [
      product.Image1,
      product.Image2,
      product.Image3,
      product.Image4,
      product.Image5,
      product.Image6,
    ].filter(Boolean);
    return list.length ? list : [product.Image1].filter(Boolean);
  }, [product]);

  useEffect(() => {
    // preload images and reset index when images change
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    setCurrentImageIndex(0);
  }, [images]);

  if (loading) return <div className="cat-center">Loading...</div>;
  if (error) return <div className="cat-error">{error}</div>;
  if (!product) return <div className="cat-center">No product found</div>;

  return (
    <div className="cat-page">
      <Seo
        title={product.ProductName || "Product"}
        description={(product.Description || `${product.ProductName} on AIO India`).slice(0, 160)}
        path={`/product-details/${product.Id}`}
        image={product.Image1 || ""}
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.ProductName || "Product",
          description: (product.Description || "").slice(0, 300),
          image: product.Image1 || "https://www.facebookindia.in/logo512.png",
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: Number(product.Price) || 0,
            availability: "https://schema.org/InStock",
            seller: { "@type": "Organization", name: "AIO India" },
          },
        }}
      />
      <div className="cat-card">
        {/* LEFT */}
        <div className="cat-left">
          <div className="cat-image-box">
            {/* image carousel: shows current image and arrows/dots when multiple */}
            <div style={{ position: "relative" }}>
              <img
                src={images.length ? images[currentImageIndex] : product.Image1}
                alt={product.ProductName}
                className="cat-image"
              />

              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    onClick={() =>
                      setCurrentImageIndex((i) =>
                        i === 0 ? images.length - 1 : i - 1
                      )
                    }
                    className="cat-image-arrow cat-image-arrow-left"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "8px",
                      transform: "translateY(-50%)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    ‹
                  </button>
                  <button
                    aria-label="Next image"
                    onClick={() =>
                      setCurrentImageIndex((i) =>
                        i === images.length - 1 ? 0 : i + 1
                      )
                    }
                    className="cat-image-arrow cat-image-arrow-right"
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "8px",
                      transform: "translateY(-50%)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "28px",
                      height: "28px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                  >
                    ›
                  </button>

                  <div
                    className="cat-dots-row"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "8px",
                      marginTop: "12px",
                      position: "static",
                    }}
                  >
                    {images.map((_, idx) => (
                      <button
                        key={idx}
                        aria-label={`Go to image ${idx + 1}`}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={
                          "cat-dot " + (idx === currentImageIndex ? "active" : "")
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="cat-floating-icons">
              {/* <FaHeart title="Wishlist" /> */}

              <div ref={shareRef} className="cat-share-wrapper">
                {/* <FaShareAlt onClick={() => setShowShare(!showShare)} /> */}
                {showShare && (
                  <div className="cat-share-menu">
                    <button onClick={shareFacebook}>
                      <FaFacebook /> Facebook
                    </button>
                    <button onClick={shareWhatsapp}>
                      <FaWhatsapp /> WhatsApp
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="cat-action-row">
            <button
              className="cat-btn cat-cart"
              onClick={handleCartToggle}
              disabled={cartUpdating}
            >
              {cartUpdating
                ? "Updating..."
                : inCart
                ? "Remove from Cart"
                : "Add to Cart"}
            </button>
            <button className="cat-btn cat-buy" onClick={handleBuyNow}>
              Buy Now
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="cat-right">
          <div className="cat-header">
            <h1>{product.ProductName}</h1>

            {isAdmin && (
              <div className="cat-admin-actions">
                <button onClick={() => navigate(`/edit-product/${productId}`)}>
                  <FaEdit /> Edit
                </button>
                <button className="danger" onClick={deleteProduct}>
                  <FaTrash /> Delete
                </button>
              </div>
            )}
          </div>

          <p className="cat-price">₹ {product.Price}</p>

          <p className="cat-desc">{product.Description}</p>

          <div className="cat-meta">
            <span>
              <strong>Seller:</strong> {product.MobileNo}
            </span>
            <span>
              <strong>Posted:</strong>{" "}
              {product.createdtime
                ? new Date(product.createdtime).toLocaleString()
                : "-"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
